"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RevenueStrategy() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: closingRef,
    isVisible: closingVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const acts = [{
    number: "PHASE 01",
    time: "AUDIT — DISCOVERY",
    title: "COMPREHENSIVE REVENUE AUDIT",
    description: "We begin by dissecting your current performance metrics. Our team analyzes your historical data, OTA positioning, and market share to identify hidden revenue leaks and untapped opportunities for growth.",
    duration: "INITIAL ASSESSMENT",
    interlude: "STRATEGY ALIGNMENT — 0 DAYS"
  }, {
    number: "PHASE 02",
    time: "STRATEGY — PLANNING",
    title: "OTA & PRICING OPTIMIZATION",
    description: "We implement data-driven dynamic pricing models and refine your OTA listings. By optimizing content, imagery, and rate parity, we ensure your property captures maximum visibility and conversion in competitive markets.",
    duration: "IMPLEMENTATION",
    interlude: "TECH INTEGRATION — 14 DAYS"
  }, {
    number: "PHASE 03",
    time: "EXECUTION — GROWTH",
    title: "TECH STACK & DIRECT BOOKINGS",
    description: "We integrate cutting-edge hospitality technology to streamline operations. Our focus shifts to driving direct bookings through targeted digital marketing and conversion-optimized website enhancements.",
    duration: "SCALING PHASE",
    interlude: "PERFORMANCE REVIEW — 30 DAYS"
  }, {
    number: "PHASE 04",
    time: "MONITORING — ANALYSIS",
    title: "CONTINUOUS REVENUE MANAGEMENT",
    description: "Revenue management is not a one-time task. We provide ongoing monitoring of market trends, competitor rates, and demand fluctuations to adjust your strategy in real-time for sustained profitability.",
    duration: "ONGOING SUPPORT",
    interlude: "QUARTERLY REVIEW — 90 DAYS"
  }, {
    number: "PHASE 05",
    time: "DIALOGUE — INSIGHTS",
    title: "STRATEGIC CONSULTATION",
    description: "We hold regular strategy sessions to discuss performance, market shifts, and long-term growth goals. Your feedback combined with our data insights ensures your property stays ahead of the curve.",
    duration: "MONTHLY SESSIONS",
    interlude: "FUTURE PLANNING — 0 DAYS"
  }, {
    number: "PHASE 06",
    time: "SUCCESS — RESULTS",
    title: "MAXIMIZED PROFITABILITY",
    description: "The final result is a high-performing asset with optimized revenue streams, improved guest acquisition costs, and a robust digital presence that stands the test of time.",
    duration: "LONG-TERM SUCCESS",
    interlude: null
  }];
  return <section data-section-id="4131" id="revenuestrategyprocess" className="bg-slate-50 text-slate-900 pt-[200px] pb-[160px] px-6 md:px-12 lg:px-20 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="font-default text-xs md:text-sm tracking-widest mb-10 md:mb-16 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-blue-600" />
            <span className="text-blue-600 font-semibold">
              REVENUE GROWTH — 01 / OUR PROCESS
            </span>
          </div>

          <h1 className="font-default font-bold leading-[0.95] tracking-tighter mb-12 md:mb-16 text-slate-900" style={{
          fontSize: "clamp(64px, 10vw, 160px)"
        }}>
            <span className="block">MAXIMIZE YOUR</span>
            <span className="block">
              <span className="font-serif italic font-light text-blue-700">REVENUE POTENTIAL.</span>
            </span>
          </h1>

          <div className="md:w-3/5">
            <p className="font-default text-xl md:text-2xl leading-relaxed text-slate-700">
              HotelRevBaBa follows a rigorous, data-backed methodology to transform your property's performance. From initial audit to sustained growth, every step is designed for maximum ROI.
            </p>
          </div>
        </div>

        {}
        <div className="mt-24 md:mt-32 relative">
          {}
          <div className="absolute left-3 md:left-[40%] top-0 bottom-0 w-[2px] bg-blue-200" aria-hidden="true" />

          <div className="space-y-16 md:space-y-24">
            {acts.map((act, i) => {
            return <ActCard key={i} data-index={i} index={i} number={act.number} time={act.time} title={act.title} description={act.description} duration={act.duration} interlude={act.interlude} isLast={i === acts.length - 1} />;
          })}
          </div>
        </div>

        {}
        <div ref={closingRef} className={`mt-24 md:mt-32 text-center transition-all duration-1000 ${closingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-serif italic text-2xl md:text-4xl text-slate-800 mb-12 md:mb-16">
            Ready to elevate your hotel's revenue? Let's start the conversation.
          </p>

          <Link to="/ContactPage">
            <Button variant="outline" size="lg" className="bg-blue-600 border-none text-white hover:bg-blue-700 font-default font-semibold tracking-wide text-sm h-14 px-10 shadow-lg group rounded-lg">
              BOOK A CONSULTATION
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}
function ActCard({
  index,
  number,
  time,
  title,
  description,
  duration,
  interlude,
  isLast,
  ...rest
}: {
  index: number;
  number: string;
  time: string;
  title: string;
  description: string;
  duration: string;
  interlude: string | null;
  isLast: boolean;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const isEven = index % 2 === 1;
  return <div ref={ref} className={`transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} {...rest}>
      <div className="relative flex items-start">
        {}
        <div className="absolute left-0 md:left-[40%] -translate-x-[11px] md:-translate-x-[11px] top-6 w-6 h-6 bg-blue-600 rounded-lg group/marker" aria-hidden="true">
          <div className="absolute inset-0 bg-blue-400 animate-pulse opacity-50 rounded-lg" />
        </div>

        {}
        <div className={`ml-12 md:ml-0 w-full md:w-[55%] ${isEven ? "md:ml-auto md:pl-20" : "md:ml-[45%] md:pl-12"}`}>
          <div className="group relative bg-white border border-slate-200 p-8 md:p-10 shadow-md hover:shadow-lg transition-all duration-500 hover:-translate-y-1 rounded-lg">
            {}
            <div className="font-default text-[11px] md:text-xs tracking-widest text-blue-600 mb-6 flex items-center gap-2 font-bold">
              <span className="inline-block w-4 h-px bg-blue-600" />
              {number} / {time}
            </div>

            {}
            <h3 className="font-default font-bold text-2xl md:text-[28px] leading-tight tracking-tight mb-5 text-slate-900">
              {title}
            </h3>

            {}
            <p className="font-default text-base md:text-lg leading-relaxed text-slate-600 mb-8">
              {description}
            </p>

            {}
            <div className="pt-6 border-t border-slate-100 font-default text-[11px] tracking-widest text-slate-400 font-semibold">
              {duration}
            </div>
          </div>
        </div>
      </div>

      {}
      {!isLast && interlude && <div className="relative mt-12 md:mt-16">
          <div className="absolute left-3 md:left-[40%] -translate-x-1/2 w-2 h-2 bg-blue-300 rounded-full" aria-hidden="true" />
          <div className={`ml-12 md:ml-0 ${!isEven ? "md:ml-auto md:pl-20 md:w-[55%]" : "md:ml-[45%] md:pl-12 md:w-[55%]"}`}>
            <span className="font-default text-[10px] md:text-xs tracking-widest text-slate-400">
              — {interlude}
            </span>
          </div>
        </div>}
    </div>;
}